import React, { useState } from 'react';
import DialogModal from '../../../components/DialogModal';
import { CircularProgress, Typography } from '@mui/material';
import DropdownSelect from '../../../components/DropdownSelect';
import { DropdownOption } from '../types';
import { CloneDeclarationWrapper, StyledButton } from '../styled';
import { academicYears } from '../../../constants/academicYears';
import { cloneDeclarations } from '../../../apis/declarationSetup';
import { RootState } from '../../../redux/store';
import { useSelector } from 'react-redux';

export interface CloneDeclarationsModalProps {
  open: boolean;
  onClose: () => void;
  gradeToCloneTo: string;
  gradeOptions: DropdownOption[];
  academicYearToCloneTo: string;
  onApiSuccess: (message: string) => void;
  onApiFailure: (message: string) => void;
}

const CloneDeclarationsModal = ({
  open,
  onClose,
  gradeToCloneTo,
  gradeOptions,
  academicYearToCloneTo,
  onApiSuccess,
  onApiFailure
}: CloneDeclarationsModalProps) => {
  const [cloneFromAcademicYear, setCloneFromAcademicYear] =
    useState<string>('');
  const [cloneFromGrade, setCloneFromGrade] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const handleClose = () => {
    setCloneFromAcademicYear('');
    setCloneFromGrade('');
    onClose();
  };

  const handleClone = async () => {
    setIsSubmitting(true);
    try {
      const cloneFrom = {
        academicYear: cloneFromAcademicYear,
        grade: cloneFromGrade
      };
      const cloneTo = {
        academicYear: academicYearToCloneTo,
        grade: gradeToCloneTo
      };
      await cloneDeclarations(loggedInUser.school, cloneFrom, cloneTo);
      onApiSuccess(
        `Declarations successfully cloned to Grade ${gradeToCloneTo}.`
      );
      handleClose();
    } catch (error: any) {
      onApiFailure(
        error?.errors?.[0]?.message || 'Failed to clone declarations.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled =
    isSubmitting || !cloneFromAcademicYear || !cloneFromGrade;

  return (
    <DialogModal
      open={open}
      onClose={handleClose}
      title={`Clone Declarations for Grade ${gradeToCloneTo}`}
      actions={
        <StyledButton onClick={handleClone} disabled={isButtonDisabled}>
          {isSubmitting ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            'Clone'
          )}
        </StyledButton>
      }
    >
      <CloneDeclarationWrapper>
        <Typography variant='h4' fontWeight='bold'>
          Clone From
        </Typography>
        <DropdownSelect
          label='Academic Year'
          placeholder='Select Academic Year'
          name='cloneFromAcademicYear'
          value={cloneFromAcademicYear}
          options={academicYears.filter(
            (academicYear) =>
              academicYear.value !== '2024-2025' &&
              academicYear.value === academicYearToCloneTo
          )}
          onChange={(e) => setCloneFromAcademicYear(e.target.value as string)}
          hasTopLabel
        />
        <DropdownSelect
          label='Grade'
          placeholder='Select Grade'
          name='cloneFromGrade'
          value={cloneFromGrade}
          options={gradeOptions.filter(
            (option: DropdownOption) =>
              !String(option.value).includes('INCOMING')
          )}
          onChange={(e) => setCloneFromGrade(e.target.value as string)}
          hasTopLabel
        />
      </CloneDeclarationWrapper>
    </DialogModal>
  );
};

export default CloneDeclarationsModal;
